import { Editor, Transforms, Path, Range, Point, Node, Element } from 'slate';
import type AhaEditor from '../../AhaEditor';
import { isKeyHotkey } from 'is-hotkey';
import {
  type CellDirection,
  getTableStructureFromSelection,
} from './TableStructure';
import TableUtils from './TableUtils';
import handleUpDownArrow from './handleUpDownArrow';
import handleShiftSelectCells from './handleShiftSelectCells';
import { isTableCellElement } from '../../types/guards';
import handleInsertableCharacterKey from './handleInsertableCharacterKey';

const isTab = isKeyHotkey('tab');
const isShiftTab = isKeyHotkey('shift+tab');
const isBackspace = isKeyHotkey('backspace');
const isDelete = isKeyHotkey('delete');
const isMovementKey = (key: string) =>
  ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key);
const isInsertableCharacter = (event: KeyboardEvent) =>
  !event.ctrlKey &&
  !event.metaKey &&
  !!event.key.match(/^[\p{L}\p{N}\p{P}\p{S}\p{Z}]$/u);

const tableMove = (
  event: KeyboardEvent,
  editor: Editor,
  direction: CellDirection,
  createNewRow = false
) => {
  if (!editor.selection) return;

  const tablePath = TableUtils.currentTablePath(editor);
  if (!tablePath) return;

  let tableStructure = getTableStructureFromSelection(editor);
  if (!tableStructure) return;

  const focusCell = Editor.above(editor, {
    match: (node) => isTableCellElement(node),
    at: editor.selection.focus,
  });
  if (!focusCell) return;

  const focusCoords = tableStructure.pathToCoordinates(focusCell[1].slice(-2));
  if (!focusCoords) return;

  let nextCell = tableStructure.nextActualCell(focusCoords, direction);

  // Create a new row and move into that.
  if (!nextCell && createNewRow) {
    TableUtils.insertRow(editor, focusCell[1]);
    tableStructure = getTableStructureFromSelection(editor);
    if (!tableStructure) return;

    nextCell = tableStructure.nextActualCell(focusCoords, direction);
    event.preventDefault();
  }

  if (!nextCell) {
    if (['left', 'up'].includes(direction)) {
      event.preventDefault();
    }
    if (['right', 'down'].includes(direction)) {
      event.preventDefault();
    }
    return;
  }

  const cellInfo = tableStructure.cell(nextCell.columnIndex, nextCell.rowIndex);
  if (!cellInfo) return;

  const [leftEdge, rightEdge] = Editor.edges(editor, [
    ...tablePath,
    ...cellInfo.relativePath,
  ]);
  Transforms.select(editor, {
    anchor: leftEdge,
    focus: rightEdge,
  });
  event.preventDefault();
};

const handleDeleteKey = (editor: Editor): boolean => {
  if (!editor.selection || !Range.isCollapsed(editor.selection)) {
    return false;
  }

  const currentBlock = Editor.above(editor, {
    at: editor.selection.focus,
    mode: 'lowest',
    match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
  });
  if (!currentBlock) return false;

  const blockEnd = Editor.end(editor, currentBlock[1]);
  if (!Point.equals(editor.selection.focus, blockEnd)) return false;

  const nextBlock = Editor.next(editor, {
    at: currentBlock[1],
    match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
  });
  if (!nextBlock) return false;

  const nextTable = TableUtils.parentTable(editor, nextBlock[1]);
  if (!nextTable) return false;

  const currentTable = TableUtils.parentTable(editor, currentBlock[1]);
  if (currentTable && Path.equals(currentTable[1], nextTable[1])) return false;

  if (Node.string(currentBlock[0]) === '') {
    Transforms.removeNodes(editor, { at: currentBlock[1] });
  }

  return true;
};

const preventDeletingTableCells = (event: KeyboardEvent, editor: Editor) => {
  if (!editor.selection || !Range.isCollapsed(editor.selection)) {
    return false;
  }

  const currentCellPath = TableUtils.currentCellPath(editor);
  if (!currentCellPath) return false;

  if (isBackspace(event)) {
    const cellStart = Editor.start(editor, currentCellPath);
    return Point.equals(editor.selection.focus, cellStart);
  } else if (isDelete(event)) {
    const cellEnd = Editor.end(editor, currentCellPath);
    return Point.equals(editor.selection.focus, cellEnd);
  }

  return false;
};

const TableKeyPlugin = {
  name: 'Table key handlers',
  handlers: {
    onKeyDown: (event: KeyboardEvent, ahaEditor: AhaEditor): boolean => {
      const { editor, controller } = ahaEditor;
      const { tableSelection } = controller.state.pluginState;
      if (!tableSelection) return false;

      if (isBackspace(event) || isDelete(event)) {
        // If there is a table selection, clear the contents of selected cells.
        if (tableSelection.multipleCellsSelected) {
          const tableStructure = getTableStructureFromSelection(editor);
          TableUtils.clearSelectedCellContents(
            editor,
            tableSelection,
            tableStructure
          );
          event.preventDefault();
          return true;
        }

        // If the cursor is at the edge of a cell, prevent deleting the cell.
        if (preventDeletingTableCells(event, editor)) {
          event.preventDefault();
          return true;
        }
      }

      // Handle pressing delete in an empty block before a table
      if (isDelete(event) && handleDeleteKey(editor)) {
        event.preventDefault();
        return true;
      }

      if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
        handleUpDownArrow(ahaEditor, event);
      }

      // If a whole table is selected, check if the anchor or focus is within a table.
      // If so, we need to insert an empty paragraph before/after to make sure the
      // entire table is removed when content is deleted or a character is inserted.
      if (
        tableSelection.hasWholeTableSelection &&
        (isDelete(event) || isBackspace(event) || isInsertableCharacter(event))
      ) {
        TableUtils.removeTablesAtStartAndEndOfSelection(editor);
      }

      if (!TableUtils.currentTable(editor)) return false;

      // If we have a table selection and the user inserts a character,
      // we should clear the contents of the selected cells and insert
      // the character into the focused cell.
      if (isInsertableCharacter(event)) {
        handleInsertableCharacterKey(ahaEditor, event);
      }

      if (isMovementKey(event.key) && event.shiftKey) {
        return handleShiftSelectCells(ahaEditor, event);
      }

      if (isShiftTab(event)) {
        tableMove(event, editor, 'left', false);
      } else if (isTab(event)) {
        tableMove(event, editor, 'right', true);
      }

      return false;
    },
  },
};

export default TableKeyPlugin;
